/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useApolloClient, useQuery } from "react-apollo";
import moment from "moment";
import _ from "lodash";

import { NumberOfbooksReadQuery, UsersReadTimeQuery } from "graphql/query/readTimeQuery";
import { checkNull, checkZero, getMonthOrderBy } from "utils/get-month";
import Breadcrumbs from "components/Common/Breadcrumb";
import NewTableList from "components/TableList/NewTable";
import ImageUi from "components/Common/ImageUi";
import { secondsToText } from "utils/seconds-to-text";
import { Config } from "config";
import gql from "graphql-tag";

const limit = 30;

const GET_USERS_QUERY = gql`
  query GET_USERS_QUERY($userIds: [uuid!]!) {
    user(where: {id: {_in: $userIds}}) {
      id 
      firstName
      lastName
      avatarUrl
      username
    }
  }
`;

const LeaderBoard = () => {
  const client = useApolloClient();
  const currentMonth = moment().format("MM"); 
  const [query, setQuery] = useState(currentMonth);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [bookReadLoading, setBookReadLoading] = useState(true);
  const [topUsersData, setTopUsersData] = useState({});
  const [currentStat, setCurrentStat] = useState(1); // 1 for LeaderBoard, 2 for Top Users With Star
  const isTopUsersWithStar = currentStat === 2;

  const handleQuery = (value) => {
    setQuery(value);
  };

  useEffect(() => {
    const authUser = localStorage.getItem("authUser");
    const token = JSON.parse(authUser)?.token;

    const fetchTopEngagingUsersData = async () => {
      try {
        const response = await fetch(`${Config.restApiUrl}/user/topEngaging`, {
          method: "GET",
          headers: {
            "Authorization": token,
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();
        setTopUsersData(data);
      } catch (err) {
        console.error("Error fetching top engaging users:", err.message);
      }
    };

    fetchTopEngagingUsersData();
  }, [currentStat]);

  const currentYear = moment().year();
  const startDate = moment(`${currentYear}-${query}-01`).toISOString();
  const endDate = moment(startDate).add(1, "month").toISOString();

  const { data, loading } = useQuery(UsersReadTimeQuery, {
    variables: {
      where: {
        userId: {
          _nin: [],
        },
        ...checkNull[query],
        ...checkZero[query],
      },
      order_by: getMonthOrderBy[query],
    },
    fetchPolicy: 'cache-and-network',
  });

  const fetchLeaderboardData = async (userId) => {
    try {
      const result = await client.query({
        query: NumberOfbooksReadQuery,
        fetchPolicy: 'network-only',
        variables: {
          start: startDate,
          end: endDate,
          userId,
        },
      });
      return result?.data?.userBook_aggregate?.aggregate?.count || 0;
    } catch (error) {
      console.error("Error fetching number of books read:", error);
      return 0;
    } finally {
      setBookReadLoading(false);
    }
  };

  useEffect(() => { 
    if (isTopUsersWithStar && topUsersData?.data?.length) {      
      const fetchUsersData = async () => {
        try {
          const statsMap = new Map(
            topUsersData.data.map(item => [
              item.userId,
              { 
                totalMessages: item.totalMessages,
                totalReadTime: item.totalReadTime 
              }
            ])
          );

          const result = await client.query({
            query: GET_USERS_QUERY,
            fetchPolicy: 'network-only',
            variables: {
              userIds: topUsersData.topUserIds, 
            },
          });

          // Merge user info + stats
          const mergedRaw = result.data.user.map(user => {
            const userId = user?.id;
            const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`;
            const username = user?.username;
            const image = user?.avatarUrl;

            const stats = statsMap.get(userId) || { 
              totalMessages: 0, 
              totalReadTime: 0 
            };

            return {
              id: userId,
              fullName,
              username,
              image,
              totalReadTime: secondsToText(stats.totalReadTime),
              totalMessages: stats.totalMessages,

              // raw (used only for sorting)
              _rt: stats.totalReadTime,
              _msg: stats.totalMessages,
            };
          });

          // Sort by readTime DESC, then messages DESC
          mergedRaw.sort((a, b) => {
            if (b._rt !== a._rt) return b._rt - a._rt;
            return b._msg - a._msg;
          });

          // Remove internal fields + add SN
          const mergedData = mergedRaw.map((item, index) => ({
            id: item.id,
            sn: index + 1,
            image: <ImageUi imageUrl={item.image} text={item.fullName} />,
            fullName: item.fullName,
            username: item.username,
            totalReadTime: item.totalReadTime,
            totalMessages: item.totalMessages,
          }));

          setLeaderboardData(mergedData);

        } catch (error) {
          console.error("Error fetching users:", error.message);
        } 
      };

      fetchUsersData(); 
    }
  }, [isTopUsersWithStar, topUsersData]);

  useEffect(() => {
    const fetchData = async () => {
      if (data?.userReadTime) {
        const leaderboardPromises = data.userReadTime.map(async (userData, index) => {
          const user = userData?.user;
          const userId = user?.id;
          const fullName = `${user?.firstName} ${user?.lastName}`;
          const username = user?.username;
          const image = user?.avatarUrl;
          const readTime = secondsToText(userData?.data[currentYear][query]) || "N/A";
          const booksRead = await fetchLeaderboardData(userId);

          return {
            id: userId,
            sn: index + 1,
            image: <ImageUi imageUrl={image} text={fullName} />,
            fullName,
            username,
            readTime,
            booksRead,
          };
        });

        const resolvedData = await Promise.all(leaderboardPromises);
        setLeaderboardData(resolvedData);
      }
    };

    if (!isTopUsersWithStar) {
      fetchData();
    }
  }, [data, query, isTopUsersWithStar]);

  const columns = [
    { id: 1, title: "S.N" },
    { id: 2, title: "Image" },
    { id: 3, title: "Full Name" },
    { id: 4, title: "Username" },
    { id: 5, title: isTopUsersWithStar ? "Total Read Time" : "Read Time" },
    { id: 6, title: isTopUsersWithStar ? "Total messages" : "No. of books read" },
  ];

  return (
    <Wrapper>
      <Breadcrumbs title="Leaderboard" breadcrumbItem="Leaderboard" />
      <NewTableList
        setQuery={handleQuery}
        placeholder="Search by name or username..."
        route="/leaderboard"
        data={leaderboardData}
        columns={columns}
        totalCount={leaderboardData.length}
        limit={limit}
        paginate
        loading={loading || bookReadLoading}
        trStyle={{
          cursor: "pointer",
        }}
        currentMonth={currentMonth}
        leaderboardMonthSelect
        currentStat={currentStat}
        setCurrentStat={setCurrentStat}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  height: 100vh;
  padding: 0 5%;
  padding-top: calc(10vh + 3%);
  overflow-y: auto;
`;

export default LeaderBoard;
